import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import { APIHOST } from '../../common';
import { ConferenceGroup } from '../../types/conferenceGroup';
import { getToken } from '../auth/AuthContext';

interface EditGroupDialogProps {
  open: boolean;
  onClose: () => void;
  conferenceId: string;
  group: ConferenceGroup | null; // null for create mode, ConferenceGroup for edit mode
  onUpdate: () => void;
}

export default function EditGroupDialog({ open, onClose, conferenceId, group, onUpdate }: EditGroupDialogProps) {

  console.debug('EditGroupDialog props:', { open, conferenceId, group });
  const [groupData, setGroupData] = useState({
    name: '',
    longName: '',
    allowLoginsAfterDateTime: '',
    accessCode: '',
    aTierAllowed: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accessCodeError, setAccessCodeError] = useState('');
  console.debug('Initial groupData state:', groupData);
  const isEditMode = !!group;

  useEffect(() => {
    console.debug('USE EFFECT:Setting groupData from group prop:', group);
      setGroupData(group || {
        name: '',
        longName: '',
        allowLoginsAfterDateTime: '',
        accessCode: '',
        aTierAllowed: 0
      });
  }, [group]);

  // Validate access code format (uppercase letters and numbers only)
  const validateAccessCode = (code: string) => {
    const regex = /^[A-Z0-9]*$/;
    return regex.test(code);
  };

  const handleSaveGroup = async () => {
    try {
      setLoading(true);
      setError('');

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const url = isEditMode 
        ? `${APIHOST}/api/conference-groups/${group?.documentId}`
        : `${APIHOST}/api/conference-groups`;
      
      const method = isEditMode ? 'PUT' : 'POST';

    
      const req: any = {
        data: {
          aTierAllowed: groupData.aTierAllowed,
          name: groupData.name,
          longName: groupData.longName,
          allowLoginsAfterDateTime: groupData.allowLoginsAfterDateTime,
          accessCode: groupData.accessCode
        }
      }

      //only send conference id on create, not update, to avoid conference being removed on update
      if (method === 'POST') {
        req.data.conference = conferenceId
      }
    
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(req),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${isEditMode ? 'update' : 'create'} conference group`);
      }

      // Close dialog and refresh
      handleClose();
      onUpdate();

    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'create'} group`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setAccessCodeError('');
    setGroupData({
      name: '',
      longName: '',
      allowLoginsAfterDateTime: '',
      accessCode: '',
      aTierAllowed: 0
    });
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{isEditMode ? 'Edit Conference Group' : 'Create New Conference Group'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mt: 2 }}>
          <TextField
            label="Group Name"
            value={groupData.name}
            onChange={(e) => setGroupData(prev => ({ ...prev, name: e.target.value }))}
            fullWidth
            required
            sx={{ mb: 2 }}
            placeholder="e.g., VIP Attendees"
            helperText="Short name for the group (e.g., HOSA TEXAS '26)"
          />
          
          <TextField
            label="Long Name"
            value={groupData.longName}
            onChange={(e) => setGroupData(prev => ({ ...prev, longName: e.target.value }))}
            fullWidth
            sx={{ mb: 2 }}
            placeholder="e.g., VIP Attendees with Special Access"
            helperText="Optional longer description for the group"
          />
          
          <TextField
            label="Access Code"
            value={groupData.accessCode}
            onChange={(e) => {
              const upperValue = e.target.value.toUpperCase();
              if (validateAccessCode(upperValue)) {
                setAccessCodeError('');
                setGroupData(prev => ({ ...prev, accessCode: upperValue }));
              } else {
                setAccessCodeError('Access code must contain only uppercase letters and numbers, must be distinct from other groups');
                setGroupData(prev => ({ ...prev, accessCode: upperValue }));
              }
            }}
            fullWidth
            required
            sx={{ mb: 2 }}
            placeholder="e.g., VIP2024"
            error={!!accessCodeError}
            helperText={accessCodeError || "Must contain only uppercase letters and numbers"}
          />
          
          <TextField
            label="A-Tier Allowed"
            type="number"
            value={groupData.aTierAllowed}
            onChange={(e) => {
              const value = Math.max(0, parseInt(e.target.value) || 0);
              setGroupData(prev => ({ ...prev, aTierAllowed: value }));
            }}
            fullWidth
            required
            sx={{ mb: 2 }}
            inputProps={{ min: 0, step: 1 }}
            helperText="Number of A-tier rooms allowed for this group. Set to 0 to disallow A-tier rooms."
          />
          
          <TextField
            label="Allow Logins After"
            type="datetime-local"
            value={groupData.allowLoginsAfterDateTime ? new Date(groupData.allowLoginsAfterDateTime).toISOString().slice(0,16) : ''}
            onChange={(e) => setGroupData(prev => ({ ...prev, allowLoginsAfterDateTime: e.target.value }))}
            fullWidth
            sx={{ mb: 2 }}
            InputLabelProps={{
              shrink: true,
            }}
            helperText="Leave empty for no restrictions"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSaveGroup} 
          variant="contained" 
          disabled={loading || !groupData.name || !groupData.accessCode || groupData.aTierAllowed < 0 || !!accessCodeError || !validateAccessCode(groupData.accessCode)}
        >
          {loading ? <CircularProgress size={20} /> : (isEditMode ? 'Update Group' : 'Create Group')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}